import { createColumnHelper } from '@tanstack/react-table';
import { checkIsDomainOrUrl } from '@ballerine/common';
import { TextWithNAFallback } from '@/components/atoms/TextWithNAFallback';
import { BallerineLink } from '@/components/atoms/BallerineLink';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const markdownComponents: Components = {
  p: ({ node, ...props }) => <p className="my-3 text-sm text-justify text-slate-800" {...props} />,
  h2: ({ node, ...props }) => (
    <h2 className="font-semibold mt-2 mb-1 text-slate-800 text-lg" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="font-semibold mt-2 mb-1 text-slate-800 text-base" {...props} />
  ),
  ul: ({ node, ...props }) => <ul className="list-disc pl-6 my-3 space-y-2" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-6 my-3 space-y-2" {...props} />,
  li: ({ node, ...props }) => <li className="pl-1.5 text-sm" {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="text-[0.7rem] py-0 px-2 bg-indigo-50 text-neutral-400 rounded-full hover:underline inline-block mx-1"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="pl-4 border-l-2 my-3 italic"
      style={{ borderColor: '#EFF2FF', color: '#14203D' }}
      {...props}
    />
  ),
  hr: ({ node, ...props }) => (
    <div className="my-5 w-full">
      <hr
        className="relative -mx-6"
        style={{ borderColor: '#EFF2FF', width: 'calc(100% + 3rem)' }}
        {...props}
      />
      <div
        className="h-4 relative -mx-6"
        style={{
          width: 'calc(100% + 3rem)',
          background: 'linear-gradient(0deg, #FFFFFF 0%, rgba(244, 246, 253, 0.4) 100%)',
        }}
      />
    </div>
  ),
  table: ({ node, ...props }) => (
    <table className="my-2 w-full border-collapse text-xs" {...props} />
  ),
  thead: ({ node, ...props }) => <thead className="bg-slate-50" {...props} />,
  tbody: ({ node, ...props }) => <tbody {...props} />,
  tr: ({ node, ...props }) => <tr className="border-b last:border-b-0" {...props} />,
  th: ({ node, ...props }) => (
    <th className="border border-slate-200 px-2 py-1 text-left font-semibold" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="border border-slate-200 px-2 py-1 align-top" {...props} />
  ),
};

const columnHelper = createColumnHelper<{
  domain: string;
  relationship: string;
  nodes: Array<{ type: string; value: string }>;
}>();

const addProtocolIfMissing = (url: string) => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `http://${url}`;
};

export const columns = [
  columnHelper.accessor('domain', {
    header: 'Domain/Entity',
    cell: info => {
      const domainOrEntity = info.getValue();

      if (checkIsDomainOrUrl(domainOrEntity)) {
        const domainWithProtocol = addProtocolIfMissing(domainOrEntity);

        return (
          <div className="min-w-0 max-w-[130px] break-words">
            <BallerineLink
              href={domainWithProtocol}
              title={domainWithProtocol}
              className="block justify-start break-words"
            >
              {domainOrEntity}
            </BallerineLink>
          </div>
        );
      }

      return (
        <div className="min-w-0 max-w-[130px] break-words">
          <TextWithNAFallback className="block break-words" title={domainOrEntity}>
            {domainOrEntity}
          </TextWithNAFallback>
        </div>
      );
    },
    size: 300,
  }),
  columnHelper.accessor('relationship', {
    header: 'Relationship Type',
    cell: info => {
      const relationship = info.getValue();

      return (
        <div className="min-w-0 max-w-[130px] break-words">
          <TextWithNAFallback className="block break-words" title={relationship}>
            {relationship}
          </TextWithNAFallback>
        </div>
      );
    },
  }),
  columnHelper.accessor('nodes', {
    header: 'Connected By',
    cell: info => {
      const nodes = info.getValue();
      const markdown =
        nodes?.map(node => `- **${node.type}**\n    ${node.value}`)?.join('\n') ?? '';

      if (!markdown) {
        return <TextWithNAFallback className="block" />;
      }

      return (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {markdown}
        </ReactMarkdown>
      );
    },
  }),
] as const;
